import cn from 'classnames'
import { OfferingSwitchConfirmation } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/programTypeButtonsControl/components/offeringSwitchConfirmation/OfferingSwitchConfirmation'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { OfferingIcon } from 'partner360/components/offeringWithIcon'

import { PROGRAM_TYPE_TAB_ICON_SIZES } from '../../../../configs/programTypeTabIconSizes'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import { getSwitchableCockpitOfferings } from '../../../../lib/getSwitchableCockpitOfferings'
import { useCanSwitchCockpitOffering, useCockpitOfferingSwitch } from '../../../../model/hooks'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'

import styles from './CockpitOfferingsSwitch.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planDeck

interface ICockpitOfferingsSwitchProps {
  partnerId: string
}

/** A gear at the end of the tab track, set off from the tabs by a hairline, opening the list of the
 *  partner's offerings in the year with a switch each. Switching one asks for confirmation; switching
 *  one on reloads the cockpit. Shown only to a user who may switch them. */
export const CockpitOfferingsSwitch = ({ partnerId }: ICockpitOfferingsSwitchProps) => {
  const t = useSafetyTranslation()
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const offerings = useMemo(
    () => getSwitchableCockpitOfferings(offeringsCatalog),
    [offeringsCatalog]
  )
  const canSwitch = useCanSwitchCockpitOffering(offerings)
  const [isOpened, setOpened] = useState(false)
  const {
    isDistributor,
    loadingOffering,
    pendingChange,
    requestChange,
    confirmChange,
    cancelChange,
  } = useCockpitOfferingSwitch(partnerId)

  if (offerings.length === 0 || !offerings.some(canSwitch)) {
    return null
  }

  const yearLabel = getCockpitFiscalYearLabel(fYear)
  const label = t(KEYS.offerings).replace('{year}', yearLabel)

  return (
    <>
      <span className={styles.divider} aria-hidden="true" />
      <AbsolutePopup
        rootClassName={styles.root}
        verticalStartPosition={TPopupVertical.Bottom}
        horizontalStartPosition={TPopupHorizontal.Left}
        visibilityCallback={(visible) => {
          setOpened(visible)
          return visible
        }}
        popupContent={({ action }) => (
          <PopPaper shadowPosition="auto" className={styles.popup}>
            <div className={styles.heading}>
              <Translation
                text={KEYS.offerings}
                replaceParams={[{ key: '{year}', value: yearLabel }]}
                span
              />
            </div>
            {offerings.map((offering) => (
              <div key={offering.programType} className={styles.row}>
                <span className={styles.icon}>
                  <OfferingIcon
                    offering={offering.programType}
                    sizes={PROGRAM_TYPE_TAB_ICON_SIZES}
                  />
                </span>
                <span className={styles.name}>
                  <Translation text={getOfferingTitleKey(offering.programType)} span />
                </span>
                <span aria-label={t(getOfferingTitleKey(offering.programType))}>
                  <Switch
                    checked={offering.enabled}
                    disabled={loadingOffering !== null || !canSwitch(offering)}
                    size="sm"
                    onChange={(flag) => {
                      action.close()
                      setOpened(false)
                      requestChange({ offering, flag })
                    }}
                  />
                </span>
              </div>
            ))}
            <p className={styles.note}>
              <Translation text={KEYS.offeringsNote} span />
            </p>
          </PopPaper>
        )}
      >
        <Tooltip inline disabled={isOpened} tipContent={() => <span>{label}</span>}>
          <button
            type="button"
            className={cn(styles.trigger, { [styles.opened]: isOpened })}
            aria-label={label}
            aria-expanded={isOpened}
            aria-haspopup="dialog"
          >
            <i className="kbase kbase-settings" aria-hidden />
          </button>
        </Tooltip>
      </AbsolutePopup>

      <OfferingSwitchConfirmation
        isShow={pendingChange !== null}
        isDistributor={isDistributor}
        state={pendingChange}
        onConfirm={confirmChange}
        onCancel={cancelChange}
      />
    </>
  )
}

CockpitOfferingsSwitch.displayName = 'CockpitOfferingsSwitch'
